import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuX, LuSearch, LuSave } from 'react-icons/lu';
import api, { nuevaClaveIdem } from '../../services/api';
import EquipoCargaCard from './EquipoCargaCard';
import PasoCierreSerie from './PasoCierreSerie';
import useBorradorCierre from '../../hooks/useBorradorCierre';
import { limpiarSerie, generarSerie } from '../../utils/serie';
import { useAuth } from '../../context/AuthContext';
import { getTodayISO } from '../../utils/dateUtils';
import { faltanFotos } from '../servicio/FotosEquipo';
import { subirFotosEquipos, fotosPorSubir, precargarFotosEquipo } from '../../utils/subirFoto';
import RepuestosBottomSheet from '../repuesto/RepuestosBottomSheet';
import { CausaReclamo } from './ReclamoEnFicha';

// Carga por N° de serie (2-oct-2026) — para clientes con tarifa mensual por volumen
// (ej. MODO AGUA). El técnico, frente a cada equipo, escribe la serie, carga trabajo,
// repuestos y fotos, y al final guarda todo junto. Sin presupuesto y sin precio: los
// servicios quedan COMPLETADO con mano de obra $0 y los cobra el cierre mensual
// (CierreMensualController). Las fotos llevan rótulo y no son obligatorias (18-oct-2026).
// Se arma un servicio por dirección (sede), así cada reporte individual es de un lugar.
// 22-oct-2026: se carga de a un equipo (lista con Pendiente / Listo, cada uno se abre tocándolo),
// todo queda como borrador en el celular ("Seguir después" no pierde nada) y las firmas van una sola
// vez en el paso final, junto con el resumen y el botón que cierra el trabajo.

const INPUT = 'w-full px-3 py-2.5 rounded-xl bg-panel text-ink text-body font-medium outline-none border border-black/[0.05] dark:border-white/[0.05] placeholder:text-muted';

// orden (opcional): visita agendada con equiposSerie — se precargan esos equipos y el
// cliente; al guardar, quien llama cierra la visita.

export default function CargaPorSerieSheet({ onClose, onGuardado, orden = null }) {
    // 1-nov-2026: cerrando una visita de un día que ya pasó, el trabajo (y su N/S) van con ese día, no con hoy
    const diaTrabajo = orden?.fechaProgramada && String(orden.fechaProgramada).slice(0, 10) < getTodayISO() ? String(orden.fechaProgramada).slice(0, 10) : null;
    const { usuario, esAdmin } = useAuth();
    const [clientesTarifa, setClientesTarifa] = useState(null); // admin: elegir cliente para un equipo nuevo
    const [serie, setSerie] = useState('');
    const [buscando, setBuscando] = useState(false);
    const [noEncontrado, setNoEncontrado] = useState(null); // serie buscada sin resultado
    const [cliente, setCliente] = useState(() => (orden?.clienteId ? { id: orden.clienteId, nombre: orden.clienteNombre, exigeFotos: true } : null)); // { id, nombre, exigeFotos }
    const [nuevaDir, setNuevaDir] = useState(null);         // { nombre, direccion } — alta de dirección
    const [sedesCliente, setSedesCliente] = useState([]);
    const [sedeAlta, setSedeAlta] = useState('');
    const [items, setItems] = useState([]);
    const itemsRef = useRef([]);
    itemsRef.current = items;
    const [repuestosDB, setRepuestosDB] = useState([]);
    const [sheetRep, setSheetRep] = useState(null);         // índice del item
    const [observaciones, setObservaciones] = useState('');
    const [firmas, setFirmas] = useState({ firmaTecnico: null, firmaCliente: null });
    const [guardando, setGuardando] = useState(false);
    const [progreso, setProgreso] = useState(null);         // { texto, pct } mientras se guarda
    const [abierto, setAbierto] = useState(null);           // serie del equipo que se está cargando
    const [paso, setPaso] = useState('equipos');            // 'equipos' → 'cierre'
    // Testeo integral A5 (7-oct-2026): se guarda de a una sede; si se corta a mitad y se
    // reintenta, las sedes ya guardadas no se vuelven a mandar (antes se duplicaba la primera),
    // y cada sede usa siempre la misma clave anti-duplicado (por si el primer intento sí llegó).
    const sedesGuardadas = useRef(new Set());
    const clavesIdem = useRef({});

    // Borrador en el celular: se puede cerrar la app o salir y seguir después donde quedó
    const { recuperado, borrar: borrarBorrador } = useBorradorCierre(
        `carga_serie_${orden?.id || 'suelta'}`,
        { items, observaciones, firmas, paso, cliente, guardadas: [...sedesGuardadas.current], claves: clavesIdem.current },
        (b) => {
            if (Array.isArray(b.items)) {
                setItems(act => [...b.items, ...act.filter(a => !b.items.some(x => x.serie === a.serie))]);
            }
            if (b.observaciones) setObservaciones(b.observaciones);
            if (b.firmas) setFirmas(b.firmas);
            if (b.paso === 'cierre') setPaso('cierre');
            if (b.cliente) setCliente(c => c || b.cliente);
            if (Array.isArray(b.guardadas)) sedesGuardadas.current = new Set(b.guardadas);
            if (b.claves) clavesIdem.current = b.claves;
        },
        items.length > 0,
    );

    useEffect(() => {
        api.get('/repuestos', { params: { page: 0, size: 1000 } }).then(r => {
            const d = r.data;
            setRepuestosDB(Array.isArray(d) ? d : (d?.content || []));
        }).catch(() => {});
    }, []);

    // Admin con un N/S que no existe y todavía sin cliente: puede elegir a qué
    // cliente con tarifa mensual pertenece y darlo de alta (5-oct-2026).
    useEffect(() => {
        if (!esAdmin || cliente || !noEncontrado || clientesTarifa) return;
        api.get('/clientes', { params: { page: 0, size: 1000 } })
            .then(r => { const d = r.data; setClientesTarifa((Array.isArray(d) ? d : (d?.content || [])).filter(c => c.tieneTarifa)); })
            .catch(() => setClientesTarifa([]));
    }, [esAdmin, cliente, noEncontrado, clientesTarifa]);

    useEffect(() => {
        if (!cliente) return;
        // Antes pedía /sedes?clienteId= y el backend devolvía TODAS las sedes
        api.get(`/sedes/cliente/${cliente.id}`)
            .then(r => { const d = r.data; setSedesCliente(Array.isArray(d) ? d : (d?.content || [])); })
            .catch(() => {});
    }, [cliente]);

    // El lugar de la visita (la sede del cliente con la misma dirección): los N/S nuevos
    // se dan de alta ahí sin preguntar (10-oct-2026: el pedido ya trae el lugar)
    const [sedeVisita, setSedeVisita] = useState(null);
    const lugarDeLaVisita = async () => {
        // Pedido de empresa: el lugar lo eligió la empresa (18-oct-2026). Comparar la dirección como
        // texto fallaba y el equipo quedaba en otro cliente ("0 eq." en el portal).
        if (orden?.id) {
            try {
                const r = await api.get(`/ordenes/${orden.id}/equipos-pedido`);
                if (r.data?.sedeId) return String(r.data.sedeId);
            } catch { /* sigue con la dirección */ }
        }
        if (!orden?.clienteId || !orden?.direccion) return null;
        try {
            const r = await api.get(`/sedes/cliente/${orden.clienteId}`);
            const l = Array.isArray(r.data) ? r.data : (r.data?.content || []);
            const norm = (x) => String(x || '').trim().toLowerCase();
            const s = l.find(x => norm(x.direccion) === norm(orden.direccion));
            return s ? String(s.id) : null;
        } catch { return null; }
    };

    // Visita con equipos ya elegidos (pedido de empresa o admin): se agregan solos al
    // abrir; los que todavía no estaban cargados se dan de alta en el lugar de la visita
    useEffect(() => {
        (async () => {
            const sede = await lugarDeLaVisita();
            setSedeVisita(sede);
            if (sede) setSedeAlta(sede);
            if (!orden?.equiposSerie) return;
            for (const s of orden.equiposSerie.split(',').map(x => limpiarSerie(x)).filter(Boolean)) {
                /* eslint-disable no-await-in-loop */
                if (sede) {
                    try {
                        const r = await api.get('/equipos/historial/para-carga', { params: { serie: s } });
                        if (!r.data?.encontrado) await api.post('/equipos', { numeroSerie: s, sedeId: Number(sede) });
                    } catch { /* si falla, queda el alta a mano */ }
                }
                await buscar(s);
                /* eslint-enable no-await-in-loop */
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Equipos del pedido que la empresa cargó sin N/S (16-oct-2026): antes no aparecían en el
    // cierre y había que adivinar cuántos eran; ahora se listan y se les genera el N/S de un toque
    const [sinSerie, setSinSerie] = useState([]);
    useEffect(() => {
        if (!orden?.id) return;
        api.get(`/ordenes/${orden.id}/equipos-pedido`)
            .then(r => setSinSerie((r.data?.equipos || []).filter(e => !e.serie)))
            .catch(() => {});
    }, [orden?.id]);
    const [generandoPara, setGenerandoPara] = useState(null);
    const generarParaPedido = async (e) => {
        setGenerandoPara(e.indice);
        try {
            const s = await generarSerie(orden?.tecnicoNombre || usuario?.nombre, itemsRef.current.map(i => i.serie), cliente?.id || orden?.clienteId, diaTrabajo);
            if (sedeVisita) {
                await api.post('/equipos', { numeroSerie: s, sedeId: Number(sedeVisita), modelo: e.modelo || null, ubicacion: e.ubicacion || null });
            } else {
                setNoEncontrado(s);   // sin el lugar de la visita: se da de alta a mano
            }
            api.patch(`/ordenes/${orden.id}/equipos-pedido`, { indice: e.indice, serie: s }).catch(() => {});
            setSinSerie(l => l.filter(x => x.indice !== e.indice));
            if (sedeVisita) await buscar(s, true);
            toast.success(`N/S generado: ${s}`);
        } catch (err) {
            toast.error(err?.response?.data?.mensaje || 'No se pudo generar el N/S');
        } finally { setGenerandoPara(null); }
    };

    // Al final de la lista, en el orden del pedido. Solo se abre si lo agregó el técnico a mano.
    const agregar = (eq, abrir = false) => {
        setItems(its => [...its, {
            serie: eq.serie, equipo: eq, trabajo: '', repuestos: [], fotoAntes: null, fotoDespues: null, fotosExtra: [], fotosRotulos: null, listo: false,
        }]);
        if (abrir) { setAbierto(eq.serie); setPaso('equipos'); }
    };

    // N/S nuevo para un equipo sin número: queda listo para darlo de alta
    const [generando, setGenerando] = useState(false);
    const generarNuevo = async () => {
        setGenerando(true);
        try {
            const s = await generarSerie(orden?.tecnicoNombre || usuario?.nombre, items.map(i => i.serie), cliente?.id || orden?.clienteId, diaTrabajo);
            setSerie('');
            // En una visita ya se sabe el lugar: se da de alta ahí sin preguntar (16-oct-2026)
            if (sedeVisita) {
                await api.post('/equipos', { numeroSerie: s, sedeId: Number(sedeVisita) });
                await buscar(s, true);
                toast.success(`N/S generado: ${s}`);
            } else {
                setNoEncontrado(s);
                toast.success(`N/S generado: ${s}. Elegí dónde está y tocá Alta.`);
            }
        } catch { toast.error('No se pudo generar el N/S'); }
        finally { setGenerando(false); }
    };

    const buscar = async (valor = serie, abrir = false) => {
        const s = limpiarSerie(valor);
        if (!s) return;
        if (itemsRef.current.some(i => i.serie.toUpperCase() === s)) { toast('Ese equipo ya está en la lista'); return; }
        setBuscando(true); setNoEncontrado(null);
        try {
            const r = await api.get('/equipos/historial/para-carga', { params: { serie: s } });
            if (!r.data?.encontrado) { setNoEncontrado(s); return; }
            // N/S de un cliente que no es el de esta visita (7-oct-2026)
            if (r.data.ajeno) { toast.error('Ese N/S es de otro cliente, no del de esta visita.', { duration: 6000 }); return; }
            if (!r.data.tarifaVolumen) {
                toast.error(`${r.data.cliente || 'Ese cliente'} no trabaja con tarifa mensual: cargá el trabajo desde su orden.`, { duration: 6000 });
                return;
            }
            const eq = r.data.equipo;
            // "495188/CORDOBA/4/COCINA" → el backend devuelve el 495188 (14-oct-2026)
            if (eq.serie && eq.serie !== s) {
                if (itemsRef.current.some(i => i.serie.toUpperCase() === eq.serie.toUpperCase())) { toast(`Es el ${eq.serie}, que ya está en la lista`); setSerie(''); return; }
                toast(`Es el equipo ${eq.serie}${eq.ubicacion ? ` (${eq.ubicacion})` : ''}`, { icon: '✓' });
            }
            if (cliente && eq.clienteId !== cliente.id) {
                toast.error(`Ese equipo es de ${eq.cliente}. En una carga va un solo cliente.`);
                return;
            }
            if (!cliente) {
                setCliente({ id: eq.clienteId, nombre: eq.cliente, exigeFotos: true });
            }
            agregar(eq, abrir);
            setSerie('');
        } catch {
            toast.error('No se pudo buscar. Revisá la señal.');
        } finally {
            setBuscando(false);
        }
    };

    // Alta rápida: equipo nuevo en una dirección de este mismo cliente.
    // Si la dirección tampoco existe, se crea ahí mismo ("Nueva dirección").
    const darDeAlta = async () => {
        let sedeId = sedeAlta;
        if (sedeAlta === '__nueva__') {
            if (!nuevaDir?.nombre?.trim() || !nuevaDir?.direccion?.trim()) { toast.error('Completá el lugar y la dirección'); return; }
            try {
                const r = await api.post('/sedes', { clienteId: cliente.id, nombreSede: nuevaDir.nombre.trim(), direccion: nuevaDir.direccion.trim() });
                sedeId = r.data?.id;
                setSedesCliente(ss => [...ss, r.data]);
            } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo crear la dirección'); return; }
        }
        if (!sedeId) { toast.error('Elegí la dirección'); return; }
        try {
            await api.post('/equipos', { numeroSerie: noEncontrado, sedeId: Number(sedeId) });
            setNuevaDir(null);
            toast.success(`Equipo ${noEncontrado} dado de alta`);
            const s = noEncontrado;
            setNoEncontrado(null); setSedeAlta(sedeVisita || '');
            await buscar(s, true);
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo dar de alta');
        }
    };

    const cambiar = (i, campo, valor) => setItems(its => its.map((it, k) => k === i ? { ...it, [campo]: valor } : it));
    const cambiarVarios = (i, c) => setItems(its => its.map((x, k) => (k === i ? { ...x, ...c, ...('trabajo' in c && !c.trabajo.trim() ? { listo: false } : {}) } : x)));
    const quitar = (i) => setItems(its => its.filter((_, k) => k !== i));
    // "Equipo listo": queda marcado y se abre el siguiente pendiente
    const marcarListo = (i) => {
        const sig = items.find((it, k) => k !== i && !it.listo);
        setItems(its => its.map((x, k) => (k === i ? { ...x, listo: true } : x)));
        setAbierto(sig ? sig.serie : null);
        // 27-oct-2026: sus fotos se empiezan a subir ya, mientras se carga el resto y se firma
        if (items[i]) precargarFotosEquipo(items[i], items[i].serie);
    };

    // En el paso de firmas, que se terminen de subir todas (también al volver a un borrador)
    useEffect(() => {
        if (paso === 'cierre') itemsRef.current.forEach(it => precargarFotosEquipo(it, it.serie));
    }, [paso]); // eslint-disable-line react-hooks/exhaustive-deps

    // Lo único obligatorio es qué se hizo: las fotos no (18-oct-2026)
    const faltantes = items.map(it => [!it.trabajo.trim() && 'qué se hizo', ...faltanFotos(it)].filter(Boolean));
    const listo = items.length > 0 && faltantes.every(f => f.length === 0);
    const cantListos = items.filter(it => it.listo && it.trabajo.trim()).length;
    const todosListos = items.length > 0 && cantListos === items.length;
    const conFirmas = !!(firmas.firmaCliente || firmas.firmaTecnico);

    const salir = () => {
        if (items.length) toast('Quedó guardado en el teléfono. Seguís cuando vuelvas a abrirlo.', { icon: '💾' });
        onClose();
    };

    const [causa, setCausa] = useState(orden?.reclamo?.causa || null);   // reclamo: por qué volvió a fallar (28-oct-2026)
    const guardar = async () => {
        if (!listo) { toast.error('Completá lo que falta en cada equipo'); return; }
        if (orden?.reclamo && !causa) { toast.error('Elegí por qué volvió a fallar'); return; }
        if (orden?.reclamo && causa !== orden.reclamo.causa) {
            try { await api.patch(`/ordenes/${orden.id}/reclamo`, { causa }); }
            catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar la causa'); return; }
        }
        if (firmas.firmaCliente && !(firmas.aclaracionCliente || '').trim()) { toast.error('Poné la aclaración de quién firma'); return; }
        setGuardando(true);
        const totalFotos = fotosPorSubir(items);
        let hechas = 0;
        const pasoFotos = () => setProgreso({ texto: `Subiendo fotos ${hechas}/${totalFotos}`, pct: 8 + (hechas / totalFotos) * 62 });
        if (totalFotos > 0) pasoFotos(); else setProgreso({ texto: 'Guardando el trabajo…', pct: 40 });
        try {
            // Todas las fotos en paralelo. Las que ya se subieron en segundo plano (o en un intento anterior) se reusan.
            const { fotos, fallaron } = await subirFotosEquipos(items, it => it.serie, () => { hechas++; pasoFotos(); });
            // Lo que sí subió queda subido (si se reintenta no se vuelve a mandar); lo que falló sigue en el celu
            const conFotos = items.map((it, k) => ({
                ...it,
                fotoAntes: fotos[k].fotoAntes || it.fotoAntes,
                fotoDespues: fotos[k].fotoDespues || it.fotoDespues,
                fotosExtra: fotos[k].fotosExtra.length === (it.fotosExtra || []).length ? fotos[k].fotosExtra : it.fotosExtra,
            }));
            setItems(conFotos);
            if (fallaron) throw new Error(`${fallaron} foto${fallaron !== 1 ? 's' : ''} no se pudo subir. Revisá la señal`);
            const porSede = {};
            conFotos.forEach(it => {
                const k = it.equipo.sedeId;
                (porSede[k] = porSede[k] || []).push(it);
            });
            let guardados = 0;
            const lugares = Object.keys(porSede);
            setProgreso({ texto: orden ? 'Guardando y cerrando la visita…' : 'Guardando el trabajo…', pct: 78 });
            for (const [sedeId, grupo] of Object.entries(porSede)) {
                if (sedesGuardadas.current.has(sedeId)) { guardados += grupo.length; continue; }
                if (!clavesIdem.current[sedeId]) clavesIdem.current[sedeId] = nuevaClaveIdem();
                await api.post('/servicios', {
                    clienteNombre: cliente.nombre,
                    sedeId: Number(sedeId),
                    sedeNombre: grupo[0].equipo.sede || '',
                    // Cerrando una visita: el trabajo es del técnico de la visita aunque lo cierre el admin
                    usuarioId: orden?.tecnicoId || usuario?.id,
                    servicioTipo: 'TECNICA',
                    estado: 'COMPLETADO',
                    ordenId: orden?.id || null,
                    // Con el último lugar, el servidor cierra la visita en el mismo momento (23-oct-2026)
                    ...(orden?.id && sedeId === lugares[lugares.length - 1] ? { cerrarVisita: true } : {}),
                    fecha: diaTrabajo || getTodayISO(),
                    observaciones: [observaciones.trim(), 'Cargado por N° de serie — se factura en el cierre mensual'].filter(Boolean).join(' | '),
                    ...(conFirmas ? { firmaCliente: firmas.firmaCliente || null, firmaTecnico: firmas.firmaTecnico || null, aclaracionCliente: firmas.firmaCliente ? (firmas.aclaracionCliente || '').trim() || null : null } : {}),
                    items: grupo.map(it => ({
                        equipoSerial: it.serie,
                        tecnico: orden?.tecnicoNombre || usuario?.nombre || 'Técnico',
                        trabajoTipo: 'REPARACION',
                        metodoPago: 'EFECTIVO',
                        trabajoRealizado: it.trabajo.trim(),
                        costo: 0,
                        repuestosUsados: it.repuestos,
                        fotoAntes: it.fotoAntes,
                        fotoDespues: it.fotoDespues,
                        fotosExtra: it.fotosExtra,
                        fotosRotulos: it.fotosRotulos || null,
                    })),
                }, { _idemKey: clavesIdem.current[sedeId] });
                sedesGuardadas.current.add(sedeId);
                guardados += grupo.length;
                setProgreso(p => ({ ...p, pct: Math.min(96, (p?.pct || 78) + 18 / lugares.length) }));
            }
            setProgreso({ texto: 'Listo', pct: 100 });
            borrarBorrador();
            toast.success(`${guardados} equipo${guardados !== 1 ? 's' : ''} registrado${guardados !== 1 ? 's' : ''}`);
            onGuardado && onGuardado();
            onClose();
        } catch (e) {
            const motivo = e?.response?.data?.mensaje || (e?.response ? '' : e?.message) || '';
            toast.error(`No se pudo guardar${motivo ? ': ' + motivo : ''}. Lo cargado queda guardado en el teléfono: probá de nuevo cuando tengas señal.`, { duration: 7000 });
        } finally {
            setGuardando(false);
            setProgreso(null);
        }
    };

    const BuscarEquipo = (
        <div className={items.length || sinSerie.length ? 'mt-4 pt-3 border-t border-black/[0.06] dark:border-white/[0.06]' : ''}>
            {(items.length > 0 || sinSerie.length > 0) && <p className="text-label font-black uppercase text-muted mb-1.5">¿Hay otro equipo?</p>}
            <form onSubmit={e => { e.preventDefault(); buscar(serie, true); }} className="flex gap-1.5 mb-2">
                <input value={serie} onChange={e => setSerie(limpiarSerie(e.target.value))} placeholder="N° de serie del equipo" className={INPUT} />
                <button type="submit" disabled={buscando || !serie.trim()}
                    className="px-4 rounded-xl font-black text-label uppercase bg-brand-red text-white active:scale-95 flex items-center gap-1.5 disabled:opacity-40">
                    <LuSearch size={15} /> {buscando ? '…' : 'Agregar'}
                </button>
            </form>
            {/* Equipo sin N/S: se genera con la regla de siempre (inicial + S + fecha, y A, B, C…) */}
            {(esAdmin || cliente) && !noEncontrado && (
                <button type="button" onClick={generarNuevo} disabled={generando}
                    className="text-caption font-bold text-secondary underline disabled:opacity-50">
                    {generando ? 'Generando…' : '¿El equipo no tiene N/S? Generar uno nuevo'}
                </button>
            )}
            {noEncontrado && (
                <div className="p-3 rounded-xl bg-page border border-black/[0.06] dark:border-white/[0.06] mt-2 text-caption">
                    <p className="font-bold text-ink mb-2">{noEncontrado} es nuevo, todavía no está cargado.</p>
                    {cliente ? (
                        <>
                            <p className="text-muted mb-1.5">Darlo de alta en una dirección de {cliente.nombre}:</p>
                            <div className="flex gap-1.5">
                                <select value={sedeAlta} onChange={e => { setSedeAlta(e.target.value); setNuevaDir(e.target.value === '__nueva__' ? { nombre: '', direccion: '' } : null); }} className={INPUT}>
                                    <option value="">Elegí la dirección…</option>
                                    <option value="__nueva__">+ Nueva dirección</option>
                                    {sedesCliente.map(s => <option key={s.id} value={s.id}>{[s.nombreSede || s.nombre, s.direccion].filter(Boolean).join(' · ')}</option>)}
                                </select>
                                <button onClick={darDeAlta} className="px-3 rounded-xl font-black text-label uppercase bg-ink text-page active:scale-95">Alta</button>
                            </div>
                            {nuevaDir && (
                                <div className="mt-2 space-y-1.5">
                                    <input value={nuevaDir.nombre} onChange={e => setNuevaDir(d => ({ ...d, nombre: e.target.value }))}
                                        placeholder="Nombre del lugar (ej: Gimnasio Núñez)" className={INPUT} />
                                    <input value={nuevaDir.direccion} onChange={e => setNuevaDir(d => ({ ...d, direccion: e.target.value }))}
                                        placeholder="Calle, número y localidad" className={INPUT} />
                                </div>
                            )}
                        </>
                    ) : esAdmin ? (
                        <>
                            <p className="text-muted mb-1.5">Si es un equipo nuevo, ¿de qué cliente es?</p>
                            <select value="" onChange={e => { const c = (clientesTarifa || []).find(x => String(x.id) === e.target.value); if (c) setCliente({ id: c.id, nombre: c.nombre, exigeFotos: true }); }} className={INPUT}>
                                <option value="">{clientesTarifa === null ? 'Cargando clientes…' : 'Elegí el cliente…'}</option>
                                {(clientesTarifa || []).map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                            </select>
                            {clientesTarifa && clientesTarifa.length === 0 && (
                                <p className="text-muted mt-1.5">No hay clientes con tarifa mensual. Cargale la tarifa al cliente primero.</p>
                            )}
                        </>
                    ) : (
                        <p className="text-muted">Revisá la serie. Si es un equipo nuevo, pedile al admin que lo cargue.</p>
                    )}
                </div>
            )}
        </div>
    );

    const total = items.length + sinSerie.length;

    return (
        <div className="fixed inset-0 z-[3000] flex items-end md:items-center md:justify-center bg-black/50 md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6">
            <div className="w-full md:max-w-lg rounded-t-3xl md:rounded-3xl p-5 bg-card max-h-[calc(var(--vh,1vh)*94)] overflow-y-auto">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-body-lg font-black text-ink">
                        {paso === 'cierre' ? 'Firmas y cierre' : orden?.id ? 'Cerrar visita' : orden ? 'Trabajo ya hecho' : 'Cargar equipos por N° de serie'}
                    </h3>
                    <button onClick={salir} aria-label="Salir (queda guardado)"
                        className="w-9 h-9 rounded-xl flex items-center justify-center bg-chip text-muted active:scale-95"><LuX size={16} /></button>
                </div>
                <p className="text-caption text-muted mb-3">
                    {cliente ? (orden?.id ? `${cliente.nombre} · sin precio, se factura en el cierre mensual` : `${cliente.nombre} · cargá el N/S de cada equipo que se atendió`) : 'Para clientes con tarifa mensual. Sin presupuesto ni precio.'}
                </p>
                {recuperado && items.length > 0 && (
                    <p className="mb-3 px-3 py-2 rounded-xl bg-chip text-caption font-bold text-ink">Seguís con lo que tenías cargado.</p>
                )}

                {orden?.reclamo && (
                    <div className="mb-3 p-3 rounded-2xl bg-panel"><CausaReclamo valor={causa} onElegir={setCausa} /></div>
                )}
                {paso === 'cierre' ? (
                    <PasoCierreSerie items={items} sinCargar={sinSerie.length} conOrden={!!orden}
                        observaciones={observaciones} onObservaciones={setObservaciones}
                        firmas={firmas} onFirmas={setFirmas} guardando={guardando} progreso={progreso}
                        onVolver={() => setPaso('equipos')} onFinalizar={guardar} />
                ) : (
                    <>
                        {total > 0 && (
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <p className="text-caption text-secondary">Cargá cada equipo. Al final, firmas y cierre.</p>
                                <p className="shrink-0 text-caption font-black text-ink">{cantListos}/{total} listos</p>
                            </div>
                        )}
                        <div className="space-y-2">
                            {items.map((it, i) => (
                                <EquipoCargaCard key={it.serie} item={it} numero={i + 1} abierto={abierto === it.serie}
                                    onAbrir={() => setAbierto(it.serie)}
                                    onCambiar={c => cambiarVarios(i, c)}
                                    onQuitar={() => { quitar(i); if (abierto === it.serie) setAbierto(null); }}
                                    onRepuestos={() => setSheetRep(i)}
                                    onListo={() => marcarListo(i)} />
                            ))}
                            {/* Equipos del pedido sin N/S: en la lista, con su botón, no en un aviso aparte */}
                            {sinSerie.map((e, k) => (
                                <div key={`sn-${e.indice}`} className="p-3 rounded-xl bg-[var(--warning-bg)] border border-[var(--estado-curso)]">
                                    <div className="flex items-center gap-3">
                                        <span className="w-7 h-7 shrink-0 rounded-full bg-chip text-ink text-label font-black flex items-center justify-center">{items.length + k + 1}</span>
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-caption font-black text-ink truncate">{e.ubicacion || e.modelo || `Equipo ${e.indice + 1}`} · sin N/S</span>
                                            {e.motivo && <span className="block text-label text-secondary truncate">{e.motivo}</span>}
                                        </span>
                                        <button type="button" onClick={() => generarParaPedido(e)} disabled={generandoPara !== null}
                                            className="shrink-0 h-9 px-3 rounded-lg bg-ink text-page text-label font-black active:scale-95 disabled:opacity-50">
                                            {generandoPara === e.indice ? 'Generando…' : 'Generar N/S'}
                                        </button>
                                    </div>
                                    <p className="mt-1.5 text-label text-[var(--warning-tx)]">Si tiene N/S, escribilo abajo en "¿Hay otro equipo?".</p>
                                </div>
                            ))}
                        </div>

                        {BuscarEquipo}

                        {items.length > 0 && (
                            <div className="mt-4 grid grid-cols-[auto_1fr] gap-2">
                                <button type="button" onClick={salir}
                                    className="h-12 px-3 rounded-xl font-black text-label uppercase bg-chip text-secondary active:scale-95 flex items-center gap-1.5">
                                    <LuSave size={14} /> Seguir después
                                </button>
                                <button type="button" onClick={() => { setAbierto(null); setPaso('cierre'); }} disabled={!todosListos}
                                    className="h-12 rounded-xl font-black text-label uppercase bg-brand-red text-white active:scale-95 disabled:opacity-40">
                                    {todosListos ? 'Siguiente: firmas y cierre' : `Faltan ${items.length - cantListos} equipo${items.length - cantListos !== 1 ? 's' : ''}`}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {sheetRep !== null && items[sheetRep] && (
                <RepuestosBottomSheet isOpen onClose={() => setSheetRep(null)}
                    repuestos={repuestosDB} seleccionados={items[sheetRep].repuestos}
                    onChange={nuevos => cambiar(sheetRep, 'repuestos', nuevos)} />
            )}
        </div>
    );
}
